import React, { useEffect, useState } from "react";
import "react-phone-input-2/lib/style.css";
import axios from "axios";
import config from "../../../config/Global.json";
import { ToastContainer, toast } from "react-toastify";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import "./AccountCreate.css";
import CustomInput from "../../custominput/CustomInput";
import CustomPhoneInput, { validateInternationalPhoneNumber } from "../../customphoninput/CustomPhoneInput";
import { useToastHandler } from "../ToastHandler";
import { capitalizeFirstLetter } from "../../../utils/helper/common";

const normalizePostalCode = (value = "") =>
  value.replace(/[^a-zA-Z0-9 -]/g, "").toUpperCase().slice(0, 10);

function AccountCreate() {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    contact_no: "",
    password: "",
    confirmPassword: "",
    store_name: "",
    address_line1: "",
    pincode: "",
    city: "",
    state: "",
    country: "",
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [customErr, setCustomErr] = useState(false);
  const [customErrMsg, setCustomErrMsg] = useState("");
  const [pincodeErrorFlag, setPincodeErrorFlag] = useState(false);
  const [pincodeErrorMsg, setPincodeErrorMsg] = useState("");
  const [firsterror, setFirsterror] = useState(false);
  const [firsterrormsg, setFirsterrormsg] = useState("");
  const [lasterror, setLasterror] = useState(false);
  const [lasterrormsg, setLasterrormsg] = useState("");
  const [passError, setPassError] = useState(false);
  const [passErrMsg, setPassErrMsg] = useState("");
  const [confpassError, setConfpassError] = useState(false);
  const [confpassErrorMsg, setConfpassErrorMsg] = useState("");
  const [contactNoError, setContactNoError] = useState(false);
  const [contactNoErrorMsg, setcontactNoErrorMsg] = useState("");
  const [phoneCountryHint, setPhoneCountryHint] = useState("");
  const [isStep1Complete, setIsStep1Complete] = useState(false);
  const [isStep2Complete, setIsStep2Complete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [emailOtp, setEmailOtp] = useState("");
  const [emailVerificationToken, setEmailVerificationToken] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleToast } = useToastHandler();
  const socialSignupToken = searchParams.get("social_signup") || "";
  const socialProvider = searchParams.get("provider") || "";
  const providerEmail = searchParams.get("email") || "";
  const isSocialSignup = Boolean(socialSignupToken);
  const providerVerifiedEmail = Boolean(isSocialSignup && providerEmail);

  useEffect(() => {
    if (!socialSignupToken) return;

    setFormData((prevData) => ({
      ...prevData,
      email: searchParams.get("email") || prevData.email,
      first_name: searchParams.get("first_name") || prevData.first_name,
      last_name: searchParams.get("last_name") || prevData.last_name,
      password: "",
      confirmPassword: "",
    }));
  }, [socialSignupToken, searchParams]);

  useEffect(() => {
    const { store_name, address_line1, pincode, city, state, country } = formData;

    const step1Complete =
      store_name.trim() !== "" &&
      address_line1.trim() !== "" &&
      pincode.trim() !== "" &&
      city.trim() !== "" &&
      state.trim() !== "" &&
      country.trim() !== "";

    setIsStep1Complete(step1Complete);
  }, [formData]);

  useEffect(() => {
    const { first_name, last_name, email, contact_no, password, confirmPassword } = formData;
    const passwordReady = isSocialSignup || (password.trim() !== "" && confirmPassword.trim() !== "" && password === confirmPassword);
    const emailReady = providerVerifiedEmail || Boolean(emailVerificationToken);

    const step2Complete =
      first_name.trim() !== "" &&
      last_name.trim() !== "" &&
      email.trim() !== "" &&
      contact_no.trim() !== "" &&
      passwordReady &&
      emailReady &&
      !firsterror &&
      !lasterror &&
      !passError &&
      !confpassError &&
      !customErr &&
      !contactNoError;

    setIsStep2Complete(step2Complete);
  }, [formData, firsterror, lasterror, passError, confpassError, customErr, contactNoError, emailVerificationToken, isSocialSignup, providerVerifiedEmail]);

  const handleSocialLogin = async (provider) => {
    try {
      const response = await axios.get(
        `${config.api.host}${config.api.socialAuthStart}${provider}/start/?mode=signup`
      );
      if (response.data?.redirect_url) {
        window.location.href = response.data.redirect_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.error || `${provider} signup is not configured yet.`);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    let formattedValue = value;

    if (name === "store_name" || name === "address_line1") {
      formattedValue = capitalizeFirstLetter(value);
    } else if (name === "first_name" || name === "last_name") {
      formattedValue = value.charAt(0).toUpperCase() + value.slice(1);
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: formattedValue,
    }));

    if (name === "email") {
      setEmailOtp("");
      setEmailVerificationToken("");
    }
  };

  const handlePhoneChange = (phone) => {
    setFormData((prevData) => ({
      ...prevData,
      contact_no: phone,
    }));
  };

  const validateTextInput = (value) => {
    const textRegex = /^[A-Za-z,.\-_]{0,255}$/;
    return textRegex.test(value);
  };

  const fetchLocationDetails = async (pincode) => {
    const apiUrl = `${config.api.host}${config.api.getPincodeData}`;

    try {
      const response = await axios.post(apiUrl, { pincode });
      const { city, state, country } = response.data;

      setFormData((prevData) => ({
        ...prevData,
        city: city || "",
        state: state || "",
        country: country || "",
      }));

      setPincodeErrorMsg("");
      setPincodeErrorFlag(false);
    } catch (error) {
      setPincodeErrorMsg("");
      setPincodeErrorFlag(false);
    }
  };

  const handlePincodeChange = (event) => {
    const postalCode = normalizePostalCode(event.target.value);

    setFormData((prevData) => ({
      ...prevData,
      pincode: postalCode,
      ...(postalCode.trim() === "" && { city: "", state: "", country: "" }),
    }));

    setPincodeErrorMsg("");
    setPincodeErrorFlag(false);

    if (postalCode.trim().length >= 3) {
      fetchLocationDetails(postalCode);
    }
  };

  const handlePincodeBlur = () => {
    const { pincode } = formData;
    const postalCode = normalizePostalCode(pincode);

    if (!postalCode.trim()) {
      setPincodeErrorMsg("Postal code is required.");
      setPincodeErrorFlag(true);
      return;
    }

    setFormData((prevData) => ({ ...prevData, pincode: postalCode }));
    setPincodeErrorMsg("");
    setPincodeErrorFlag(false);
    fetchLocationDetails(postalCode);
  };

  const validatePasswords = () => {
    const { password, confirmPassword } = formData;

    if (password.length < 8) {
      handleToast({
        type: "ERROR",
        message: "Password must be at least 8 characters long.",
      });
      return false;
    }

    if (confirmPassword.length < 8) {
      handleToast({
        type: "ERROR",
        message: "Confirm password must be at least 8 characters long.",
      });
      return false;
    }

    if (password !== confirmPassword) {
      handleToast({
        type: "ERROR",
        message: "Passwords do not match.",
      });
      return false;
    }

    return true;
  };

  const handleNextButtonClick = () => {
    if (currentStep === 1) {
      if (isStep1Complete) {
        setCurrentStep(2);
      } else {
        handleToast({
          type: "ERROR",
          message: "Please fill in all store details correctly.",
        });
      }
      return;
    }

    if (currentStep === 2) {
      if (isStep2Complete) {
        handleSubmit();
      } else {
        handleToast({
          type: "ERROR",
          message: "Please fill in all account fields correctly.",
        });
      }
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;

    if (name === "confirmPassword") {
      const { password, confirmPassword } = formData;

      if (password !== confirmPassword) {
        setErrors((prevErrors) => ({ ...prevErrors, confirmPassword: true }));
        setConfpassError(true);
        setConfpassErrorMsg("Password does not match.");
      } else {
        setErrors((prevErrors) => ({ ...prevErrors, confirmPassword: false }));
        setConfpassError(false);
        setConfpassErrorMsg("");
      }
    } else if (name === "password") {
      const passwordRegex =
        /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>_-])[A-Za-z\d!@#$%^&*(),.?":{}|<>_-]{8,}$/;

      if (!passwordRegex.test(value)) {
        setErrors((prevErrors) => ({ ...prevErrors, password: true }));
        setPassError(true);
        setPassErrMsg(
          "Password must be at least 8 characters, with one uppercase letter, one digit, and one special character."
        );
      } else {
        setPassError(false);
        setPassErrMsg("");
        setErrors((prevErrors) => ({ ...prevErrors, password: false }));
      }
    } else if (name === "first_name") {
      if (!validateTextInput(value)) {
        setFirsterror(true);
        setFirsterrormsg("Special characters and numbers are not allowed.");
        setErrors((prevErrors) => ({
          ...prevErrors,
          first_name: true,
        }));
      } else {
        setFirsterror(false);
        setFirsterrormsg("");
        setErrors((prevErrors) => ({
          ...prevErrors,
          first_name: false,
        }));
      }
    } else if (name === "last_name") {
      if (!validateTextInput(value)) {
        setLasterror(true);
        setLasterrormsg("Special characters and numbers are not allowed.");
        setErrors((prevErrors) => ({
          ...prevErrors,
          last_name: true,
        }));
      } else {
        setLasterror(false);
        setLasterrormsg("");
        setErrors((prevErrors) => ({
          ...prevErrors,
          last_name: false,
        }));
      }
    }
  };

  const validatePhoneNo = (value) => {
    let errorMessage = "";

    if (!value) {
      errorMessage = "Phone number is required.";
    } else if (!validateInternationalPhoneNumber(value)) {
      errorMessage = "Please enter a valid phone number for the selected country.";
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      contact_no: errorMessage,
    }));

    setContactNoError(Boolean(errorMessage));
    setcontactNoErrorMsg(errorMessage);
  };

  const checkUserExist = async (email) => {
    const emailRegex = /^[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/;
    let hasError = false;

    if (!emailRegex.test(email)) {
      setErrors((prevErrors) => ({ ...prevErrors, email: true }));
      setCustomErr(true);
      setCustomErrMsg("Please enter a valid email.");
      hasError = true;
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, email: false }));
      setCustomErr(false);
      setCustomErrMsg("");
    }

    if (!hasError) {
      try {
        const response = await axios.post(`${config.api.host}${config.api.userExist}`, {
          email,
        });

        if (response.data.exists) {
          setErrors((prevErrors) => ({ ...prevErrors, email: true }));
          setCustomErr(true);
          setCustomErrMsg("This user account already exists.");
        } else {
          setErrors((prevErrors) => ({ ...prevErrors, email: false }));
          setCustomErr(false);
          setCustomErrMsg("");
        }
      } catch (error) {
        handleToast({
          type: "ERROR",
          message: "Unable to verify email. Please try again.",
        });
      }
    }
  };

  const requestEmailOtp = async () => {
    if (!formData.email || errors.email || customErr) {
      handleToast({ type: "ERROR", message: "Please enter a valid email before requesting OTP." });
      return;
    }

    setOtpSending(true);
    try {
      await axios.post(`${config.api.host}${config.api.signupEmailOtpRequest}`, {
        email: formData.email,
      });
      handleToast({ type: "SUCCESS", message: "Verification code sent to your email." });
    } catch (error) {
      handleToast({
        type: "ERROR",
        message: error.response?.data?.error || "Unable to send verification code.",
      });
    } finally {
      setOtpSending(false);
    }
  };

  const verifyEmailOtp = async () => {
    if (!emailOtp.trim()) {
      handleToast({ type: "ERROR", message: "Please enter the verification code." });
      return;
    }

    setOtpVerifying(true);
    try {
      const response = await axios.post(`${config.api.host}${config.api.signupEmailOtpVerify}`, {
        email: formData.email,
        otp: emailOtp,
      });
      setEmailVerificationToken(response.data?.verification_token || "");
      handleToast({ type: "SUCCESS", message: "Email verified successfully." });
    } catch (error) {
      handleToast({
        type: "ERROR",
        message: error.response?.data?.error || "Unable to verify code.",
      });
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleSubmit = async () => {
    if (currentStep !== 2) {
      setCurrentStep(currentStep + 1);
      return;
    }

    if (!isSocialSignup && !validatePasswords()) {
      return;
    }

    if (!providerVerifiedEmail && !emailVerificationToken) {
      handleToast({ type: "ERROR", message: "Please verify your email before continuing." });
      return;
    }

    setSubmitting(true);

    const apiUrl = `${config.api.host}${config.api.signUpUser}`;

    try {
      const response = await axios.post(apiUrl, {
        ...formData,
        email_verification_token: emailVerificationToken,
        social_signup_token: socialSignupToken,
      });
      const storeId = response?.data?.store?.id;

      handleToast({
        type: "SUCCESS",
        message: "User created successfully!",
      });

      navigate("/subscription-page", { state: { storeId } });

      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        contact_no: "",
        password: "",
        confirmPassword: "",
        store_name: "",
        address_line1: "",
        pincode: "",
        city: "",
        state: "",
        country: "",
      });
    } catch (error) {
      if (error.response && error.response.data.error) {
        const errorMessage = error.response.data.error;

        if (errorMessage.includes("A user with this contact number already exists")) {
          handleToast({
            type: "ERROR",
            message: "This mobile number is already registered. Please try with a different one.",
          });
        } else if (errorMessage.includes("duplicate key value violates unique constraint")) {
          handleToast({
            type: "ERROR",
            message: "This email is already registered. Please try with a different one.",
          });
        } else {
          handleToast({
            type: "ERROR",
            message: "Error creating user.",
          });
        }
      } else {
        handleToast({
          type: "ERROR",
          message: "Error creating user.",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
      return;
    }

    navigate("/login");
  };

  const stepTitle = currentStep === 1 ? "Create Store" : "Owner Account";
  const stepSubtitle =
    currentStep === 1
      ? "Add your store details to build your business workspace."
      : "Create the owner login account to continue.";
  const isButtonEnabled = currentStep === 1 ? isStep1Complete : isStep2Complete;

  const renderSocialButtons = () => (
    <>
      <div className="auth-social-grid">
        <button type="button" className="auth-social-btn" onClick={() => handleSocialLogin("google")}>
          <span className="auth-google-logo">G</span>
          Gmail
        </button>

        <button type="button" className="auth-social-btn" onClick={() => handleSocialLogin("facebook")}>
          <span className="auth-facebook-logo">f</span>
          Facebook
        </button>
      </div>

      <div className="auth-divider">
        <span />
        <p>or continue manually</p>
        <span />
      </div>
    </>
  );

  const renderStep1 = () => (
    <div className="signup-field-stack">
      <div className="auth-field">
        <label htmlFor="storeName">Store Name</label>
        <CustomInput
          type="text"
          id="storeName"
          name="store_name"
          placeholder="Enter store name"
          value={formData.store_name}
          onChange={handleChange}
          required
          error={Boolean(errors.store_name)}
          helpertext={errors.store_name || ""}
        />
      </div>

      <div className="auth-field">
        <label htmlFor="storeAddress">Store Address</label>
        <CustomInput
          type="text"
          id="storeAddress"
          name="address_line1"
          placeholder="Enter store address"
          value={formData.address_line1}
          onChange={handleChange}
          required
          error={Boolean(errors.address_line1)}
          helpertext={errors.address_line1 || ""}
        />
      </div>

      <div className="auth-two-grid">
        <div className="auth-field">
          <label htmlFor="pincode">Pin Code</label>
          <CustomInput
            type="text"
            id="pincode"
            name="pincode"
            maxLength={10}
            placeholder="Postal / pin code"
            value={formData.pincode}
            onChange={handlePincodeChange}
            onBlur={handlePincodeBlur}
            required
            error={pincodeErrorFlag}
            custom={pincodeErrorFlag}
            msg={pincodeErrorMsg}
          />
        </div>

        <div className="auth-field">
          <label htmlFor="city">City</label>
          <CustomInput
            type="text"
            id="city"
            name="city"
            placeholder="City"
            value={formData.city}
            onChange={handleChange}
            required
            error={Boolean(errors.city)}
            helpertext={errors.city || ""}
          />
        </div>
      </div>

      <div className="auth-two-grid">
        <div className="auth-field">
          <label htmlFor="state">State</label>
          <CustomInput
            type="text"
            id="state"
            name="state"
            placeholder="State"
            value={formData.state}
            onChange={handleChange}
            required
            error={Boolean(errors.state)}
            helpertext={errors.state || ""}
          />
        </div>

        <div className="auth-field">
          <label htmlFor="country">Country</label>
          <CustomInput
            type="text"
            id="country"
            name="country"
            placeholder="Country"
            value={formData.country}
            onChange={handleChange}
            required
            error={Boolean(errors.country)}
            helpertext={errors.country || ""}
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="signup-field-stack">
      <div className="auth-two-grid">
        <div className="auth-field">
          <label htmlFor="firstName">First Name</label>
          <CustomInput
            type="text"
            id="firstName"
            name="first_name"
            placeholder="First name"
            value={formData.first_name}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            error={Boolean(errors.first_name)}
            helpertext={errors.first_name || ""}
            custom={firsterror}
            msg={firsterrormsg}
          />
        </div>

        <div className="auth-field">
          <label htmlFor="lastName">Last Name</label>
          <CustomInput
            type="text"
            id="lastName"
            name="last_name"
            placeholder="Last name"
            value={formData.last_name}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            error={Boolean(errors.last_name)}
            helpertext={errors.last_name || ""}
            custom={lasterror}
            msg={lasterrormsg}
          />
        </div>
      </div>

      <div className="auth-field">
        <label htmlFor="phonenumber" className="auth-phone-label">
          Phone Number
          {phoneCountryHint && (
            <span>{phoneCountryHint}</span>
          )}
        </label>
        <CustomPhoneInput
          required
          type="number"
          id="phonenumber"
          name="contact_no"
          placeholder="Phone number"
          value={formData.contact_no}
          onChange={handlePhoneChange}
          onBlur={() => validatePhoneNo(formData.contact_no)}
          error={Boolean(errors.contact_no)}
          helpertext={errors.contact_no}
          custom={contactNoError}
          msg={contactNoErrorMsg}
          onCountryChange={(country) =>
            setPhoneCountryHint(country ? `You are entering ${country.adjective.toLowerCase()} number` : "")
          }
        />
      </div>

      <div className="auth-field">
        <label htmlFor="email">Email Address</label>
        <CustomInput
          type="email"
          id="email"
          name="email"
          placeholder="Business email"
          value={formData.email}
          onChange={handleChange}
          onBlur={(event) => checkUserExist(event.target.value)}
          disabled={providerVerifiedEmail}
          custom={customErr}
          msg={customErrMsg}
          required
          error={Boolean(errors.email)}
          helpertext={errors.email || ""}
        />
      </div>

      {providerVerifiedEmail ? (
        <div className="auth-verification-card verified">
          <strong>{socialProvider || "Social"} email verified</strong>
          <span>You can continue without creating a password.</span>
        </div>
      ) : (
        <div className="auth-verification-card">
          <div>
            <strong>Email verification</strong>
            <span>We will send a 6 digit code before creating your store account.</span>
          </div>
          <div className="auth-otp-row">
            <button type="button" onClick={requestEmailOtp} disabled={otpSending || customErr || !formData.email}>
              {otpSending ? "Sending..." : "Send OTP"}
            </button>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={emailOtp}
              onChange={(event) => setEmailOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="Enter code"
            />
            <button type="button" onClick={verifyEmailOtp} disabled={otpVerifying || emailOtp.length !== 6}>
              {emailVerificationToken ? "Verified" : otpVerifying ? "Checking..." : "Verify"}
            </button>
          </div>
        </div>
      )}

      {!isSocialSignup && (
        <div className="auth-two-grid">
          <div className="auth-field">
            <label htmlFor="password">Password</label>
            <CustomInput
              type="password"
              id="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              error={Boolean(errors.password)}
              helpertext={errors.password || ""}
              custom={passError}
              msg={passErrMsg}
            />
          </div>

          <div className="auth-field">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <CustomInput
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              placeholder="Confirm password"
              value={formData.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              error={Boolean(errors.confirmPassword)}
              helpertext={errors.confirmPassword || ""}
              custom={confpassError}
              msg={confpassErrorMsg}
            />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="auth-form auth-signup-form">
      <ToastContainer />
      <style>{signupStyles}</style>

      <button type="button" className="signup-back-btn" onClick={handleBack}>
        <span>←</span>
        {currentStep === 2 ? "Back to Store" : "Back to Login"}
      </button>

      <div className="auth-form-header">
        <div className="auth-form-badge">Step {currentStep} of 2</div>
        <h1>{stepTitle}</h1>
        <p>{stepSubtitle}</p>
      </div>

      <div className="signup-stepper">
        <div className={`signup-step ${currentStep >= 1 ? "active" : ""}`}>
          <span>1</span>
          <p>Store</p>
        </div>
        <div className="signup-step-line">
          <i style={{ width: currentStep === 2 ? "100%" : "0%" }} />
        </div>
        <div className={`signup-step ${currentStep === 2 ? "active" : ""}`}>
          <span>2</span>
          <p>Owner</p>
        </div>
      </div>

      {currentStep === 1 && renderSocialButtons()}

      <form
        className="auth-field-stack"
        onSubmit={(event) => {
          event.preventDefault();
          handleNextButtonClick();
        }}
      >
        {currentStep === 1 ? renderStep1() : renderStep2()}

        <button
          type="button"
          data-testid={currentStep === 2 ? "select-plan-button" : "next-button"}
          className="auth-primary-btn"
          onClick={handleNextButtonClick}
          disabled={!isButtonEnabled || submitting}
          style={{
            opacity: isButtonEnabled && !submitting ? 1 : 0.58,
            cursor: isButtonEnabled && !submitting ? "pointer" : "not-allowed",
          }}
        >
          {submitting ? "Creating Account..." : currentStep === 2 ? "Select Plan" : "Continue"}
        </button>
      </form>

      <div className="auth-bottom-text">
        <span>Already have an account?</span>
        <Link to="/login">Login</Link>
      </div>
    </div>
  );
}

export default AccountCreate;

const signupStyles = `
  .auth-form {
    width: 100%;
  }

  .signup-back-btn {
    border: none;
    background: transparent;
    color: #0d532f;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 0;
    margin-bottom: 14px;
    font-size: 13px;
    font-weight: 950;
    cursor: pointer;
  }

  .signup-back-btn span {
    font-size: 18px;
    line-height: 1;
  }

  .auth-form-header {
    margin-bottom: 16px;
  }

  .auth-form-badge {
    display: inline-flex;
    padding: 7px 11px;
    border-radius: 10px;
    margin-bottom: 11px;
    color: #0d532f;
    font-size: 10px;
    font-weight: 950;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
  }

  .auth-form-header h1 {
    margin: 0;
    color: #0f172a;
    font-size: 32px;
    line-height: 1.05;
    font-weight: 950;
    letter-spacing: -0.06em;
  }

  .auth-form-header p {
    margin: 7px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
    line-height: 1.55;
  }

  .signup-stepper {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 10px;
    margin-bottom: 15px;
    padding: 10px;
    border-radius: 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
  }

  .signup-step {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #94a3b8;
    font-size: 12px;
    font-weight: 950;
  }

  .signup-step span {
    width: 28px;
    height: 28px;
    border-radius: 999px;
    background: #e2e8f0;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 950;
  }

  .signup-step p {
    margin: 0;
  }

  .signup-step.active {
    color: #0d532f;
  }

  .signup-step.active span {
    background: #0d532f;
    color: #ffffff;
  }

  .signup-step-line {
    height: 4px;
    border-radius: 999px;
    background: #e2e8f0;
    overflow: hidden;
  }

  .signup-step-line i {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: #0d532f;
    transition: width 0.25s ease;
  }

  .auth-social-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 9px;
    margin-bottom: 14px;
  }

  .auth-social-btn {
    height: 46px;
    border: 1px solid #dbe3ec;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    background: #ffffff;
    color: #0f172a;
    font-size: 12px;
    font-weight: 950;
    cursor: pointer;
    transition: transform 0.16s ease, box-shadow 0.16s ease, border 0.16s ease;
  }

  .auth-social-btn:hover {
    transform: translateY(-1px);
    border-color: #94a3b8;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.10);
  }

  .auth-google-logo,
  .auth-facebook-logo {
    width: 21px;
    height: 21px;
    border-radius: 7px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 950;
  }

  .auth-google-logo {
    color: #ea4335;
    background: #fff1f2;
  }

  .auth-facebook-logo {
    color: #ffffff;
    background: #1877f2;
    font-family: Arial, sans-serif;
    font-size: 16px;
  }

  .auth-divider {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 10px;
    margin: 4px 0 14px;
  }

  .auth-divider span {
    height: 1px;
    background: #e2e8f0;
  }

  .auth-divider p {
    margin: 0;
    color: #94a3b8;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .auth-verification-card {
    display: grid;
    gap: 12px;
    padding: 14px;
    border: 1px solid #dbe3ec;
    border-radius: 14px;
    background: #f8fafc;
  }

  .auth-verification-card.verified {
    background: #ecfdf5;
    border-color: #86efac;
  }

  .auth-verification-card strong {
    display: block;
    color: #0f172a;
    font-size: 13px;
    font-weight: 950;
    text-transform: capitalize;
  }

  .auth-verification-card span {
    display: block;
    margin-top: 4px;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
    line-height: 1.5;
  }

  .auth-otp-row {
    display: grid;
    grid-template-columns: auto minmax(120px, 1fr) auto;
    gap: 8px;
  }

  .auth-otp-row button {
    min-height: 42px;
    border: 0;
    border-radius: 10px;
    padding: 0 14px;
    background: #0d532f;
    color: #ffffff;
    font-size: 12px;
    font-weight: 950;
    cursor: pointer;
  }

  .auth-otp-row button:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .auth-otp-row input {
    min-height: 42px;
    border: 1px solid #dbe3ec;
    border-radius: 10px;
    padding: 0 12px;
    color: #0f172a;
    font-size: 15px;
    font-weight: 850;
    letter-spacing: 0.18em;
    background: #ffffff;
  }

  .auth-field-stack,
  .signup-field-stack {
    display: grid;
    gap: 12px;
  }

  .auth-two-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .auth-field label {
    display: block;
    margin-bottom: 7px;
    color: #334155;
    font-size: 12px;
    font-weight: 950;
    letter-spacing: 0.02em;
  }

  .auth-field .auth-phone-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .auth-phone-label span {
    color: #0f766e;
    font-size: 11px;
    font-weight: 850;
    letter-spacing: 0;
    text-transform: none;
  }

  .auth-field input,
  .auth-field .form-control {
    height: 50px !important;
    min-height: 50px !important;
    border-radius: 10px !important;
    border-color: #cbd5e1 !important;
    font-weight: 750;
    box-shadow: none !important;
  }

  .auth-field input:focus,
  .auth-field .form-control:focus {
    border-color: #0d532f !important;
    box-shadow: 0 0 0 4px rgba(13, 83, 47, 0.10) !important;
  }

  .react-tel-input .form-control {
    width: 100% !important;
    height: 50px !important;
    border-radius: 10px !important;
  }

  .auth-primary-btn {
    width: 100%;
    height: 50px;
    border: none;
    border-radius: 12px;
    margin-top: 2px;
    background: linear-gradient(135deg, #0d532f 0%, #052e1a 100%);
    color: #ffffff;
    font-size: 15px;
    font-weight: 950;
    box-shadow: 0 18px 34px rgba(13, 83, 47, 0.22);
    transition: transform 0.16s ease, box-shadow 0.16s ease;
  }

  .auth-primary-btn:not(:disabled):hover {
    transform: translateY(-1px);
    box-shadow: 0 22px 42px rgba(13, 83, 47, 0.28);
  }

  .auth-bottom-text {
    display: flex;
    justify-content: center;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: 16px;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }

  .auth-bottom-text a {
    color: #0d532f;
    text-decoration: none;
    font-weight: 950;
  }

  .auth-bottom-text a:hover {
    text-decoration: underline;
  }

  @media (max-width: 560px) {
    .auth-form-header h1 {
      font-size: 28px;
    }

    .auth-two-grid,
    .auth-social-grid,
    .signup-stepper {
      grid-template-columns: 1fr;
    }

    .signup-step-line {
      display: none;
    }
  }
`;
